// tab/src/context/UserContext.tsx
// OnRounds Phase 2 - User Context Provider

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';
import { api } from '../services/api';

// Types
interface User {
  id: string;
  organisationId: string;
  teamsUserId: string;
  email: string;
  displayName: string;
  qualifications: string[];
  isAdmin: boolean;
  isSupervisor: boolean;
}

interface CurrentShift {
  id: string;
  teamId: string;
  roleId: string;
  roleName: string;
  clockOnTime: string;
  plannedEndTime: string | null;
}

interface UserContextType {
  user: User | null;
  currentShift: CurrentShift | null;
  isLoading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  refreshShift: (teamId: string) => Promise<void>;
  setCurrentShift: (shift: CurrentShift | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: 'UserProvider rendering' }) }).catch(() => {});
  
  const [user, setUser] = useState<User | null>(null);
  const [currentShift, setCurrentShift] = useState<CurrentShift | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = useCallback(async () => {
    fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: 'refreshUser started' }) }).catch(() => {});
    
    try {
      setIsLoading(true);
      setError(null);
      
      let teamsUserId = '';
      let displayName = 'Unknown User';
      
      // Get Azure AD ID from Teams context
      try {
        const context = await microsoftTeams.app.getContext();
        teamsUserId = context.user?.id || '';
        displayName = context.user?.displayName || displayName;
        
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `Teams context OK: userId=${teamsUserId}` }) }).catch(() => {});
        
      } catch (e) {
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `Teams SDK error: ${e}` }) }).catch(() => {});
        
        if (process.env.NODE_ENV === 'development') {
          teamsUserId = 'dev-test-user';
        }
      }
      
      if (!teamsUserId) {
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: 'No teamsUserId - returning early' }) }).catch(() => {});
        setError('Could not get user identity from Teams');
        return;
      }
      
      // Fetch user from backend
      fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `Calling getCurrentUser with: ${teamsUserId}` }) }).catch(() => {});
      
      const result = await api.getCurrentUser(teamsUserId);
      
      fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `getCurrentUser result: ${JSON.stringify(result.user?.id || 'null')}` }) }).catch(() => {});
      
      if (result.user) {
        setUser({
          id: result.user.id,
          organisationId: result.user.orgId || '',
          teamsUserId,
          email: result.user.email || '',
          displayName: result.user.name || displayName,
          qualifications: result.user.qualifications || [],
          isAdmin: result.user.isAdmin || false,
          isSupervisor: result.user.isSupervisor || false,
        });
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: 'User set successfully' }) }).catch(() => {});
      } else {
        fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: 'User not found in database' }) }).catch(() => {});
        setError('User not registered. Please ask an admin to add you via /adduser command.');
      }
    } catch (err: any) {
      fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: `refreshUser error: ${err.message}` }) }).catch(() => {});
      setError('Failed to load user information');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshShift = useCallback(async (teamId: string) => {
    try {
      // Get the teamsUserId from the current user state
      if (!user?.teamsUserId) {
        console.log('No user teamsUserId for refreshShift');
        setCurrentShift(null);
        return;
      }
      
      const result = await api.getCurrentShift(teamId, user.teamsUserId);
      
      if (result.clockedOn && result.shift && result.role) {
        setCurrentShift({
          id: result.shift.id,
          teamId: result.shift.teamId,
          roleId: result.shift.roleId,
          roleName: result.role.name,
          clockOnTime: result.shift.clockOnTime,
          plannedEndTime: result.shift.plannedEndTime || null,
        });
      } else {
        setCurrentShift(null);
      }
    } catch (err) {
      console.error('Failed to load current shift:', err);
      setCurrentShift(null);
    }
  }, [user?.teamsUserId]);

  useEffect(() => {
    fetch('/api/debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: 'UserProvider useEffect fired' }) }).catch(() => {});
    refreshUser();
  }, [refreshUser]);

  return (
    <UserContext.Provider
      value={{
        user,
        currentShift,
        isLoading,
        error,
        refreshUser,
        refreshShift,
        setCurrentShift,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
